/**
 * Сервис для загрузки списков экспертов и известных личностей с share-server
 */

const SHARE_SERVER_URL = import.meta.env.VITE_SHARE_SERVER_URL || 'http://localhost:3002';

export type ListsLanguage = 'ru' | 'en';

// Кэш для списков по языку: lang -> ListItem[]
const expertsCache: Record<ListsLanguage, ListItem[] | null> = { ru: null, en: null };
const personsCache: Record<ListsLanguage, ListItem[] | null> = { ru: null, en: null };

// Promise-based кэширование для предотвращения дублирования запросов (по языку)
const expertsLoadingPromise: Record<ListsLanguage, Promise<ListItem[]> | null> = { ru: null, en: null };
const personsLoadingPromise: Record<ListsLanguage, Promise<ListItem[]> | null> = { ru: null, en: null };
let listsLoadingPromise: Promise<{ experts: ListItem[]; persons: ListItem[] }> | null = null;
let listsLoadingLang: ListsLanguage | null = null;

export interface ListItem {
  value: string;
  label: string;
}

export interface ExpertsResponse {
  experts: ListItem[];
}

export interface PersonsResponse {
  persons: ListItem[];
}

/**
 * Fallback список экспертов
 * Используется когда не удается загрузить список с сервера
 */
const getFallbackExperts = (): ListItem[] => [
  { value: 'бизнес-консультант', label: 'Бизнес-консультант' },
  { value: 'финансовый аналитик', label: 'Финансовый аналитик' },
  { value: 'карьерный консультант', label: 'Карьерный консультант' },
  { value: 'психолог', label: 'Психолог' },
  { value: 'юрист', label: 'Юрист' },
  { value: 'маркетолог', label: 'Маркетолог' },
  { value: 'IT-специалист', label: 'IT-специалист' },
  { value: 'врач', label: 'Врач' },
  { value: 'педагог', label: 'Педагог' },
  { value: 'архитектор', label: 'Архитектор' },
  { value: 'дизайнер', label: 'Дизайнер' },
  { value: 'инженер', label: 'Инженер' },
  { value: 'менеджер-проектов', label: 'Менеджер проектов' },
  { value: 'HR-специалист', label: 'HR-специалист' },
  { value: 'продакт-менеджер', label: 'Продакт-менеджер' },
  { value: 'стратегический-консультант', label: 'Стратегический консультант' },
  { value: 'тренер', label: 'Тренер' },
  { value: 'коуч', label: 'Коуч' },
  { value: 'журналист', label: 'Журналист' },
  { value: 'писатель', label: 'Писатель' },
  { value: 'custom', label: 'Другой эксперт...' }
];

function normalizeLang(lang: string | undefined): ListsLanguage {
  return lang === 'ru' || lang === 'en' ? lang : 'en';
}

/**
 * Загружает список экспертов с share-server для указанного языка
 */
export async function loadExperts(lang: ListsLanguage | string | undefined, forceReload: boolean = false): Promise<ListItem[]> {
  const l = normalizeLang(lang);
  // Возвращаем кэшированные данные, если не требуется принудительная перезагрузка
  if (!forceReload && expertsCache[l]) {
    console.log(`📋 Используем кэшированный список экспертов (lang=${l})`);
    return expertsCache[l];
  }

  // Если уже идет загрузка для этого языка, возвращаем существующий Promise
  if (!forceReload && expertsLoadingPromise[l]) {
    console.log(`📋 Ожидаем завершения текущей загрузки экспертов (lang=${l})...`);
    return expertsLoadingPromise[l];
  }

  // Создаем новый Promise для загрузки
  expertsLoadingPromise[l] = (async () => {
    try {
      console.log(`📋 Загружаем список экспертов с share-server (lang=${l})...`);
      
      const response = await fetch(`${SHARE_SERVER_URL}/api/experts?lang=${l}`, {
        method: 'GET',
        credentials: 'include', // Включаем cookies для CORS
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ExpertsResponse = await response.json();
      console.log(`📋 Загружено ${data.experts.length} экспертов (lang=${l})`);
      
      // Добавляем опцию "Другой эксперт..."
      const expertsWithCustom = [
        ...data.experts,
        { value: 'custom', label: 'Другой эксперт...' }
      ];
      
      expertsCache[l] = expertsWithCustom;
      
      return expertsWithCustom;
    } catch (error) {
      console.error('❌ Ошибка загрузки списка экспертов:', error);
      
      console.log('📋 Используем fallback список экспертов из-за ошибки загрузки');
      const fallbackList = getFallbackExperts();
      
      expertsCache[l] = fallbackList;
      
      return fallbackList;
    } finally {
      expertsLoadingPromise[l] = null;
    }
  })();

  return expertsLoadingPromise[l];
}

/**
 * Fallback список известных личностей
 * Используется когда не удается загрузить список с сервера
 */
const getFallbackPersons = (): ListItem[] => [
  // Российские деятели
  { value: 'Александр Пушкин', label: 'Александр Пушкин' },
  { value: 'Лев Толстой', label: 'Лев Толстой' },
  { value: 'Михаил Ломоносов', label: 'Михаил Ломоносов' },
  { value: 'Дмитрий Менделеев', label: 'Дмитрий Менделеев' },
  { value: 'Владимир Путин', label: 'Владимир Путин' },
  { value: 'Дмитрий Медведев', label: 'Дмитрий Медведев' },
  
  // Российские бизнесмены
  { value: 'Алексей Мордашов', label: 'Алексей Мордашов' },
  { value: 'Герман Греф', label: 'Герман Греф' },
  { value: 'Владимир Потанин', label: 'Владимир Потанин' },
  { value: 'Павел Дуров', label: 'Павел Дуров' },
  { value: 'Евгений Касперский', label: 'Евгений Касперский' },
  
  // Зарубежные IT-предприниматели
  { value: 'Билл Гейтс', label: 'Билл Гейтс' },
  { value: 'Стив Джобс', label: 'Стив Джобс' },
  { value: 'Илон Маск', label: 'Илон Маск' },
  { value: 'Марк Цукерберг', label: 'Марк Цукерберг' },
  { value: 'Джефф Безос', label: 'Джефф Безос' },
  { value: 'Сергей Брин', label: 'Сергей Брин' },
  { value: 'Ларри Пейдж', label: 'Ларри Пейдж' },
  
  // Зарубежные бизнесмены
  { value: 'Уоррен Баффет', label: 'Уоррен Баффет' },
  { value: 'Джек Ма', label: 'Джек Ма' },
  { value: 'Ричард Брэнсон', label: 'Ричард Брэнсон' },
  { value: 'Опра Уинфри', label: 'Опра Уинфри' },
  
  // Ученые и изобретатели
  { value: 'Альберт Эйнштейн', label: 'Альберт Эйнштейн' },
  { value: 'Исаак Ньютон', label: 'Исаак Ньютон' },
  { value: 'Никола Тесла', label: 'Никола Тесла' },
  { value: 'Томас Эдисон', label: 'Томас Эдисон' },
  { value: 'Стивен Хокинг', label: 'Стивен Хокинг' },
  
  // Политические деятели
  { value: 'Уинстон Черчилль', label: 'Уинстон Черчилль' },
  { value: 'Авраам Линкольн', label: 'Авраам Линкольн' },
  { value: 'Нельсон Мандела', label: 'Нельсон Мандела' },
  
  // Философы и мыслители
  { value: 'Сократ', label: 'Сократ' },
  { value: 'Аристотель', label: 'Аристотель' },
  { value: 'Конфуций', label: 'Конфуций' },
  { value: 'Сунь-цзы', label: 'Сунь-цзы' },
  
  // Вымышленные персонажи
  { value: 'Мастер Йода', label: 'Мастер Йода' },
  { value: 'Шерлок Холмс', label: 'Шерлок Холмс' },
  { value: 'custom', label: 'Другая личность...' }
];

/**
 * Загружает список известных личностей с share-server для указанного языка
 */
export async function loadPersons(lang: ListsLanguage | string | undefined, forceReload: boolean = false): Promise<ListItem[]> {
  const l = normalizeLang(lang);
  if (!forceReload && personsCache[l]) {
    console.log(`👥 Используем кэшированный список известных личностей (lang=${l})`);
    return personsCache[l];
  }

  if (!forceReload && personsLoadingPromise[l]) {
    console.log(`👥 Ожидаем завершения текущей загрузки личностей (lang=${l})...`);
    return personsLoadingPromise[l];
  }

  personsLoadingPromise[l] = (async () => {
    try {
      console.log(`👥 Загружаем список известных личностей с share-server (lang=${l})...`);
      
      const response = await fetch(`${SHARE_SERVER_URL}/api/persons?lang=${l}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: PersonsResponse = await response.json();
      console.log(`👥 Загружено ${data.persons.length} известных личностей (lang=${l})`);
      
      const personsWithCustom = [
        ...data.persons,
        { value: 'custom', label: 'Другая личность...' }
      ];
      
      personsCache[l] = personsWithCustom;
      
      return personsWithCustom;
    } catch (error) {
      console.error('❌ Ошибка загрузки списка известных личностей:', error);
      
      console.log('👥 Используем fallback список известных личностей из-за ошибки загрузки');
      const fallbackList = getFallbackPersons();
      
      personsCache[l] = fallbackList;
      
      return fallbackList;
    } finally {
      personsLoadingPromise[l] = null;
    }
  })();

  return personsLoadingPromise[l];
}

/**
 * Загружает оба списка параллельно для указанного языка
 */
export async function loadLists(lang: ListsLanguage | string | undefined, forceReload: boolean = false): Promise<{ experts: ListItem[]; persons: ListItem[] }> {
  const l = normalizeLang(lang);
  if (!forceReload && listsLoadingPromise && listsLoadingLang === l) {
    console.log(`📋👥 Ожидаем завершения текущей загрузки списков (lang=${l})...`);
    return listsLoadingPromise;
  }

  listsLoadingLang = l;
  listsLoadingPromise = (async () => {
    try {
      console.log(`📋👥 Загружаем списки экспертов и личностей (lang=${l})...`);
      
      const [experts, persons] = await Promise.all([
        loadExperts(l, forceReload),
        loadPersons(l, forceReload)
      ]);
      
      console.log(`📋👥 Загружено ${experts.length} экспертов и ${persons.length} личностей (lang=${l})`);
      
      return { experts, persons };
    } catch (error) {
      console.error('❌ Ошибка загрузки списков:', error);
      
      console.log('📋👥 Используем fallback списки из-за ошибки загрузки');
      const fallbackExperts = getFallbackExperts();
      const fallbackPersons = getFallbackPersons();
      
      expertsCache[l] = fallbackExperts;
      personsCache[l] = fallbackPersons;
      
      return { 
        experts: fallbackExperts, 
        persons: fallbackPersons 
      };
    } finally {
      listsLoadingPromise = null;
      listsLoadingLang = null;
    }
  })();

  return listsLoadingPromise;
}

/**
 * Очищает кэш списков (для принудительной перезагрузки)
 */
export function clearListsCache(): void {
  console.log('🗑️ Очищаем кэш списков');
  expertsCache.ru = null;
  expertsCache.en = null;
  personsCache.ru = null;
  personsCache.en = null;
  expertsLoadingPromise.ru = null;
  expertsLoadingPromise.en = null;
  personsLoadingPromise.ru = null;
  personsLoadingPromise.en = null;
  listsLoadingPromise = null;
  listsLoadingLang = null;
}

/**
 * Принудительно перезагружает все списки для указанного языка
 */
export async function reloadLists(lang: ListsLanguage | string | undefined): Promise<{ experts: ListItem[]; persons: ListItem[] }> {
  console.log('🔄 Принудительная перезагрузка списков');
  clearListsCache();
  return loadLists(lang, true);
}
